import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { PlannerStore } from '../../../services/planner-store';
import { loadPlacesLibrary } from '../../../services/gmaps-loader';

@Component({
  selector: 'app-start-point',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './start-point.component.html',
  styleUrl: './start-point.component.css',
})
export class StartPointComponent implements AfterViewInit, OnDestroy {
  @ViewChild('acHost', { static: false }) acHost!: ElementRef<HTMLDivElement>;

  readonly store = inject(PlannerStore);

  private autocomplete: google.maps.places.PlaceAutocompleteElement | null = null;
  errorMessage: string | null = null;

  async ngAfterViewInit(): Promise<void> {
    try {
      const places = await loadPlacesLibrary();
      // No includedPrimaryTypes: cities AND villages/localities stay selectable;
      // only the country is constrained.
      this.autocomplete = new places.PlaceAutocompleteElement({
        includedRegionCodes: ['rs'],
        requestedRegion: 'RS',
        requestedLanguage: 'sr-Latn',
        // Drop the built-in magnifier; we overlay our own starting-point icon.
        noInputIcon: true,
      });
      this.autocomplete.setAttribute('placeholder', 'Unesi polazište ...');
      this.acHost.nativeElement.appendChild(this.autocomplete);
      this.autocomplete.addEventListener('gmp-select', this.onSelect);
    } catch (e) {
      this.errorMessage = 'Pretraga polazišta trenutno nije dostupna.';
      console.error('[start-point] init failed', e);
    }
  }

  ngOnDestroy(): void {
    this.autocomplete?.removeEventListener('gmp-select', this.onSelect);
    this.autocomplete?.remove();
    this.autocomplete = null;
  }

  clear(): void {
    this.store.clearStartPoint();
    if (this.autocomplete) this.autocomplete.value = '';
  }

  // Arrow fn so `this` stays bound when used as an event listener. Typed as a
  // plain EventListener because the element's 'gmp-select' overload doesn't
  // resolve through the base HTMLElement signature.
  private onSelect = async (event: Event): Promise<void> => {
    const place = (event as google.maps.places.PlacePredictionSelectEvent).placePrediction.toPlace();
    await place.fetchFields({ fields: ['displayName', 'location', 'formattedAddress'] });
    const loc = place.location;
    if (!loc) return;
    this.store.setStartPoint({
      name: place.displayName ?? place.formattedAddress ?? 'Polazište',
      lat: loc.lat(),
      lng: loc.lng(),
    });
    // Reset the input so the chip below is the single source of truth.
    if (this.autocomplete) this.autocomplete.value = '';
  };
}
